import { AccountPicker } from "../components/AccountPicker";
import { Empty, Panel } from "../components/Panel";
import { useRebates } from "../hooks/useRebates";
import { accountOnlyProblem } from "../lib/accounts";
import {
  climbVerdict,
  dayRows,
  holdVerdict,
  modelCheck,
  paidRows,
  progress,
  ratePercent,
  toneColor,
  warnings,
} from "../lib/rebates";
import { formatDollars } from "../lib/tape";
import { useCommandTarget } from "../store/useAccounts";

/**
 * Раздел «тир и возврат тейкеру».
 *
 * Отвечает на три вопроса, и порядок панелей — это их порядок по важности.
 *
 * Первый: сколько нам вернули. Это факт, он приходит с ленты выплат и от нашей
 * модели не зависит вовсе.
 *
 * Второй: удержится ли нынешняя ступень. Он идёт РАНЬШЕ «сколько до
 * следующей», потому что окно скользящее: объём вытекает из тридцати суток сам
 * собой, и ступень теряют молча.
 *
 * Третий: стоит ли докатывать. Ответ на него почти всегда «нет», и это не
 * пессимизм, а арифметика — ступень удешевляет комиссию, а не платит за объём.
 */
export function RebatesPage() {
  // Выбор аккаунта и здесь, а не только на странице рынка: тир у каждого
  // аккаунта свой, и сравнивать их, бегая на чужую страницу, неудобно.
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <AccountPicker />
      </div>
      <RebatesBody />
    </div>
  );
}

function RebatesBody() {
  const q = useRebates();
  const r = q.data;
  // Тир — у аккаунта: без выбора аккаунта (в том числе при когорте) раздел
  // молчал бы «загрузкой» вечно.
  const problem = accountOnlyProblem(useCommandTarget(), "тир");

  if (!r || problem) {
    return (
      <Panel title="Возврат тейкеру">
        <Empty>{problem ?? (q.isError ? "раздел не загрузился" : "загрузка…")}</Empty>
      </Panel>
    );
  }
  if (!r.known) {
    return (
      <Panel title="Возврат тейкеру" subtitle="тир программы Polymarket">
        <Empty>{r.reason ?? "объём не посчитан"}</Empty>
      </Panel>
    );
  }

  const hold = holdVerdict(r);
  const climb = climbVerdict(r);
  const check = modelCheck(r);
  const notes = warnings(r);
  const rows = dayRows(r);
  const paid = paidRows(r);
  const share = progress(r);

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={`Тир ${r.tier.name}`}
        subtitle={
          `возврат ${ratePercent(r.tier.rebate_bps)} от уплаченной тейкерской комиссии · ` +
          `окно ${r.window_days} суток, пересчёт в 00:00 UTC`
        }
        actions={
          <span className="num text-xs text-[var(--color-muted)]">
            {r.wallet.slice(0, 10)}…
          </span>
        }
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Figure
            label="взвешенный объём окна"
            value={formatDollars(r.weighted_usd)}
            hint={r.complete ? `порог ${r.tier.name}: ${formatDollars(r.tier.threshold_usd)}` : "занижен: см. оговорки"}
          />
          <Figure
            label="уплачено комиссии"
            value={formatDollars(r.fee_usd)}
            hint={`темп ${formatDollars(r.pace_usd)} в сутки за ${r.pace_days} суток`}
          />
          <Figure
            label="запас над порогом"
            value={formatDollars(r.tier.headroom_usd)}
            hint={`завтра из окна выпадет ${formatDollars(r.drop_off_usd)} wV`}
          />
        </div>

        {r.next ? (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded bg-[var(--color-border)]">
              <div
                className="h-full bg-[var(--color-accent)]"
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-[var(--color-muted)]">
              от порога {r.tier.name} ({formatDollars(r.tier.threshold_usd)}) до {r.next.name} (
              {formatDollars(r.next.threshold_usd)}) пройдено{" "}
              <span className="num">{Math.round(share * 100)} %</span>
            </p>
          </div>
        ) : null}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Сколько набрано" subtitle={`фактические переводы за ${r.window_days} суток`}>
          {paid.length === 0 ? (
            <Empty>{r.paid.reason ?? "выплат не видели"}</Empty>
          ) : (
            <>
              <table className="w-full text-xs">
                <tbody>
                  {paid.map((row) => (
                    <tr key={row.label} className="border-t border-[var(--color-border)] first:border-0">
                      <td className="py-1.5">{row.label}</td>
                      <td className="num py-1.5 text-right">{row.value}</td>
                      <td className="py-1.5 pl-3 text-[var(--color-muted)]">{row.hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {check ? (
                <div className="mt-3 border-t border-[var(--color-border)] pt-3">
                  <p className="text-xs" style={{ color: toneColor(check.tone) }}>
                    {check.headline}
                  </p>
                  <p className="text-xs text-[var(--color-muted)]">{check.detail}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Сверка идёт с ОЖИДАНИЕМ модели, а не с частным «пришло на уплачено»:
                    внутри окна ступень меняется, и частное даёт величину, которой нет ни
                    в одной строке программы.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </Panel>

        <Panel title="Удержать и взять" subtitle="порог окна — требование непрерывное">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-xs" style={{ color: toneColor(hold.tone) }}>
                {hold.headline}
              </p>
              <p className="text-xs text-[var(--color-muted)]">{hold.detail}</p>
              {r.hold.lost_at ? (
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  перестань мы торговать сегодня — ступень уйдёт{" "}
                  <span className="num">{r.hold.lost_at.slice(0, 10)}</span>
                </p>
              ) : null}
            </div>

            <div className="border-t border-[var(--color-border)] pt-3">
              <p className="text-xs" style={{ color: toneColor(climb.tone) }}>
                {climb.headline}
              </p>
              <p className="text-xs text-[var(--color-muted)]">{climb.detail}</p>
              {r.next ? (
                <table className="mt-2 w-full text-xs">
                  <tbody className="text-[var(--color-muted)]">
                    <tr>
                      <td className="py-0.5">не хватает объёма</td>
                      <td className="num py-0.5 text-right">{formatDollars(r.next.gap_usd)}</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">он же в комиссии</td>
                      <td className="num py-0.5 text-right">{formatDollars(r.next.gap_fee_usd)}</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">
                        он же в нотионале по {formatDollars(r.avg_price)}
                      </td>
                      <td className="num py-0.5 text-right">
                        {formatDollars(r.next.gap_notional_usd)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5">и удерживать, в сутки</td>
                      <td className="num py-0.5 text-right">{formatDollars(r.next.daily_fee_usd)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : null}
              <p className="mt-2 text-xs text-[var(--color-muted)]">
                Доллар нотионала даёт <span className="num">{formatDollars(r.wv_per_dollar)}</span>{" "}
                объёма по нашей средней цене {formatDollars(r.avg_price)}. Дешёвая сторона книги
                даёт его в разы быстрее: множитель равен весу категории на (1 − цена).
              </p>
            </div>
          </div>
        </Panel>
      </div>

      {notes.length > 0 ? (
        <Panel title="Чего расчёт не знает" subtitle="границы применимости чисел выше">
          <ul className="flex flex-col gap-1 text-xs text-[var(--color-muted)]">
            {notes.map((n) => (
              <li key={n}>— {n}</li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title="По суткам" subtitle="свежие сверху">
        {rows.length === 0 ? (
          <Empty>обход ещё не собрал ни одних суток</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">сутки</th>
                  <th className="pb-2 text-right font-normal">сделок</th>
                  <th className="pb-2 text-right font-normal">комиссия</th>
                  <th className="pb-2 text-right font-normal">объём wV</th>
                  <th className="pb-2 font-normal">состояние</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className="border-t border-[var(--color-border)]">
                    <td className="num py-1.5">{row.day}</td>
                    <td className="num py-1.5 text-right">{row.trades}</td>
                    <td className="num py-1.5 text-right">{row.fee}</td>
                    <td className="num py-1.5 text-right">{row.weighted}</td>
                    <td className="py-1.5" style={{ color: toneColor(row.tone) }}>
                      {row.note}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** Крупное число с подписью: три таких стоят в шапке раздела. */
function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div>
      <p className="text-xs text-[var(--color-muted)]">{label}</p>
      <p className="num text-lg">{value}</p>
      <p className="text-xs text-[var(--color-muted)]">{hint}</p>
    </div>
  );
}
